'use client';

import SpinnerIcon from '@/components/icons/SpinnerIcon';
import { useCart } from '@/contexts/CartContext';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';

// Give up on the redirect after this and let the visitor click again, in case
// checkout never loads while they are still sitting on the cart.
const MAX_REDIRECT_MS = 10000;

const CartCheckoutButton = ({ id = 'checkout-button' }) => {
  const { cart } = useCart();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const checkoutUrl = cart?.checkoutUrl;
  const isDisabled = !checkoutUrl || isRedirecting;

  // Checkout is on another origin, so clicking unloads this page. Pressing back
  // restores it from the back/forward cache *without remounting*, which is why
  // this is a pageshow listener and not a mount effect — the component never
  // re-runs, so isRedirecting stayed true and left the button unclickable
  // (BL-492). A page that is genuinely reloaded remounts and resets on its own.
  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) setIsRedirecting(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  useEffect(() => {
    if (!isRedirecting) return undefined;
    const timer = setTimeout(() => setIsRedirecting(false), MAX_REDIRECT_MS);
    return () => clearTimeout(timer);
  }, [isRedirecting]);

  return (
    <a
      href={checkoutUrl || undefined}
      id={id}
      data-cy="checkout-button"
      rel="nofollow noindex"
      aria-disabled={isDisabled}
      aria-busy={isRedirecting}
      onClick={(e) => {
        if (isDisabled) {
          e.preventDefault();
          return;
        }
        setIsRedirecting(true);
      }}
      className={`flex w-full items-center justify-center rounded-full bg-sea px-4 py-4 text-white ${
        isDisabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer'
      }`}
    >
      {isRedirecting ? (
        <SpinnerIcon className="mr-2 h-5 w-5 shrink-0 animate-spin" />
      ) : (
        <LockClosedIcon className="mr-2 inline-block h-5 w-5" aria-hidden="true" />
      )}
      <span>Checkout</span>
    </a>
  );
};

export default CartCheckoutButton;
